import { 
  doc, 
  getDoc,
  setDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { RouteFeedback, RouteAggregatedFeedback, RouteFeedbackHelper } from '../../models/climbing/RouteFeedback';

export class RouteFeedbackService {
  
  // Helper to get the feedback document reference for a gym
  private static getFeedbackDocRef(gymId: string) {
    return doc(db, 'gyms', gymId, 'feedback', 'current');
  }
  
  // Get all feedback for a gym from the single document
  private static async getGymFeedbackDocument(gymId: string): Promise<RouteFeedback[]> {
    try {
      const feedbackRef = this.getFeedbackDocRef(gymId);
      const feedbackDoc = await getDoc(feedbackRef);
      
      if (feedbackDoc.exists()) {
        const data = feedbackDoc.data();
        return data.feedbacks || [];
      }
      
      return [];
    } catch (error) {
      console.error('Error getting gym feedback document:', error);
      throw error;
    }
  }

  // Save all feedback for a gym to the single document
  private static async saveGymFeedbackDocument(gymId: string, feedbacks: RouteFeedback[]): Promise<void> {
    try {
      const feedbackRef = this.getFeedbackDocRef(gymId);
      await setDoc(feedbackRef, { 
        feedbacks,
        lastUpdated: Date.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving gym feedback document:', error);
      throw error;
    }
  }

  // Create new feedback or update existing one from the same climber for the same route
  static async createOrUpdateFeedback(gymId: string, feedback: Omit<RouteFeedback, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const now = Date.now();
      let feedbacks = await this.getGymFeedbackDocument(gymId);
      
      // Check if this climber already has feedback for this route
      const existingIndex = feedbacks.findIndex(f => 
        f.climberId === feedback.climberId && f.routeId === feedback.routeId
      );
      
      let feedbackId: string;
      
      if (existingIndex !== -1) {
        // Update existing feedback
        feedbackId = feedbacks[existingIndex].id;
        feedbacks[existingIndex] = {
          ...feedbacks[existingIndex],
          ...feedback,
          updatedAt: now
        };
      } else {
        // Create new feedback
        feedbackId = `${feedback.climberId}_${feedback.routeId}_${now}`;
        const newFeedback: RouteFeedback = {
          id: feedbackId,
          ...feedback,
          createdAt: now,
          updatedAt: now
        };
        feedbacks.push(newFeedback);
      }
      
      await this.saveGymFeedbackDocument(gymId, feedbacks);
      return feedbackId;
    } catch (error) {
      console.error('Error creating/updating route feedback:', error);
      throw error;
    }
  }

  // Delete feedback
  static async deleteFeedback(gymId: string, feedbackId: string): Promise<void> {
    try {
      let feedbacks = await this.getGymFeedbackDocument(gymId);
      feedbacks = feedbacks.filter(f => f.id !== feedbackId);
      await this.saveGymFeedbackDocument(gymId, feedbacks);
    } catch (error) {
      console.error('Error deleting route feedback:', error);
      throw error;
    }
  }

  // Get feedback by ID
  static async getFeedback(gymId: string, feedbackId: string): Promise<RouteFeedback | null> {
    try {
      const feedbacks = await this.getGymFeedbackDocument(gymId);
      return feedbacks.find(f => f.id === feedbackId) || null;
    } catch (error) {
      console.error('Error getting route feedback:', error);
      throw error;
    }
  }

  // Get all feedback for a specific route
  static async getRouteFeedbacks(gymId: string, routeId: string): Promise<RouteFeedback[]> {
    try {
      const feedbacks = await this.getGymFeedbackDocument(gymId);
      return feedbacks
        .filter(f => f.routeId === routeId)
        .sort((a, b) => b.createdAt - a.createdAt); // Sort by newest first
    } catch (error) {
      console.error('Error getting route feedbacks:', error);
      throw error;
    }
  }

  // Get feedback from a specific climber for a route
  static async getClimberFeedbackForRoute(gymId: string, routeId: string, climberId: string): Promise<RouteFeedback | null> {
    try {
      const feedbacks = await this.getGymFeedbackDocument(gymId);
      return feedbacks.find(f => f.routeId === routeId && f.climberId === climberId) || null;
    } catch (error) {
      console.error('Error getting climber feedback for route:', error);
      throw error;
    }
  }

  // Get all feedback for a gym
  static async getGymFeedbacks(gymId: string): Promise<RouteFeedback[]> {
    try {
      const feedbacks = await this.getGymFeedbackDocument(gymId);
      return feedbacks.sort((a, b) => b.createdAt - a.createdAt); // Sort by newest first
    } catch (error) {
      console.error('Error getting gym feedbacks:', error);
      throw error;
    }
  }

  // Get aggregated feedback for a route
  static async getRouteAggregatedFeedback(gymId: string, routeId: string): Promise<RouteAggregatedFeedback> {
    try {
      const feedbacks = await this.getRouteFeedbacks(gymId, routeId);
      return RouteFeedbackHelper.aggregateRouteFeedback(routeId, feedbacks);
    } catch (error) {
      console.error('Error getting route aggregated feedback:', error);
      throw error;
    }
  }

  // Get aggregated feedback for multiple routes
  static async getMultipleRoutesAggregatedFeedback(gymId: string, routeIds: string[]): Promise<RouteAggregatedFeedback[]> {
    try {
      const allFeedbacks = await this.getGymFeedbackDocument(gymId);
      
      return routeIds.map(routeId => {
        const routeFeedbacks = allFeedbacks.filter(f => f.routeId === routeId);
        return RouteFeedbackHelper.aggregateRouteFeedback(routeId, routeFeedbacks);
      });
    } catch (error) {
      console.error('Error getting multiple routes aggregated feedback:', error);
      throw error;
    }
  }

  // Check if climber can give feedback (based on gym settings)
  static async canClimberGiveFeedback(
    gymId: string,
    routeId: string, 
    climberId: string, 
    requireClimbToFeedback: boolean = false
  ): Promise<boolean> {
    try {
      // Check if feedback already exists (climbers can update their existing feedback)
      const existingFeedback = await this.getClimberFeedbackForRoute(gymId, routeId, climberId);
      
      // If gym requires climbing the route first, check climb history
      if (requireClimbToFeedback && !existingFeedback) {
        // This would require integration with your existing climb tracking system
        // For now, we'll assume climber can give feedback
        // TODO: Integrate with climb history service
        return true;
      }

      return true; // Allow giving feedback or updating existing feedback
    } catch (error) {
      console.error('Error checking if climber can give feedback:', error);
      return false;
    }
  }

  // Migrate existing subcollection data to single document (run once)
  static async migrateFromSubcollection(gymId: string): Promise<void> {
    try {
      console.log(`Starting migration for gym ${gymId}...`);
      
      // This method would be used to migrate from the old subcollection structure
      // to the new single document structure. Since you're implementing this new,
      // this method can be removed or used for future migrations.
      
      console.log(`Migration completed for gym ${gymId}`);
    } catch (error) {
      console.error('Error migrating feedback data:', error);
      throw error;
    }
  }
}
